// src/components/offline/OfflineIndicator.tsx
import React from 'react';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff, AlertCircle } from 'lucide-react';

interface OfflineIndicatorProps {
  isOnline: boolean;
  pendingChanges?: number;
  isSyncing?: boolean;
  lastSyncAt?: Date | null;
  onSync?: () => void;
  variant?: 'bar' | 'badge' | 'minimal';
  className?: string;
}

const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  isOnline,
  pendingChanges = 0,
  isSyncing = false,
  lastSyncAt,
  onSync,
  variant = 'bar',
  className = '',
}) => {
  const formatLastSync = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Minimal variant (just an icon)
  if (variant === 'minimal') {
    return (
      <div className={`flex items-center ${className}`}>
        {isOnline ? (
          <Wifi className="h-4 w-4 text-green-500" />
        ) : (
          <WifiOff className="h-4 w-4 text-red-500" />
        )}
        {pendingChanges > 0 && (
          <span className="ml-1 text-xs bg-amber-500 text-white px-1 rounded-full">
            {pendingChanges}
          </span>
        )}
      </div>
    );
  }

  // Badge variant
  if (variant === 'badge') {
    return (
      <div
        className={`
          inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
          ${isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
          ${className}
        `}
      >
        {isOnline ? (
          <>
            <Cloud className="h-3 w-3 mr-1" />
            Online
          </>
        ) : (
          <>
            <CloudOff className="h-3 w-3 mr-1" />
            Offline
          </>
        )}
        {pendingChanges > 0 && (
          <span className="ml-1 bg-amber-500 text-white px-1.5 rounded-full">
            {pendingChanges}
          </span>
        )}
      </div>
    );
  }

  // Bar variant (full status bar)
  if (!isOnline) {
    return (
      <div className={`bg-red-600 text-white px-4 py-2 ${className}`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center">
            <WifiOff className="h-5 w-5 mr-2" />
            <span className="font-medium">You're offline</span>
            {pendingChanges > 0 && (
              <span className="ml-2 text-sm opacity-90">
                ({pendingChanges} changes pending sync)
              </span>
            )}
          </div>
          <span className="text-sm opacity-75">
            Changes will sync when you're back online
          </span>
        </div>
      </div>
    );
  }

  // Online with pending changes
  if (pendingChanges > 0) {
    return (
      <div className={`bg-amber-500 text-white px-4 py-2 ${className}`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center">
            {isSyncing ? (
              <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            <span className="font-medium">
              {isSyncing ? 'Syncing...' : `${pendingChanges} changes pending`}
            </span>
          </div>
          {!isSyncing && onSync && (
            <button
              onClick={onSync}
              className="text-sm bg-white/20 hover:bg-white/30 px-3 py-1 rounded-md transition-colors"
            >
              Sync Now
            </button>
          )}
        </div>
      </div>
    );
  }

  // Online and synced - show minimal indicator
  return (
    <div className={`bg-green-600 text-white px-4 py-1 text-sm ${className}`}>
      <div className="flex items-center justify-center">
        <Cloud className="h-4 w-4 mr-1" />
        <span>Connected</span>
        {lastSyncAt && (
          <span className="ml-2 opacity-75">• Last sync: {formatLastSync(lastSyncAt)}</span>
        )}
      </div>
    </div>
  );
};

export default OfflineIndicator;

// Sync Status Badge for individual records
interface SyncStatusBadgeProps {
  status: 'pending' | 'syncing' | 'synced' | 'conflict' | 'error';
  size?: 'sm' | 'md';
  showLabel?: boolean;
  className?: string;
}

export const SyncStatusBadge: React.FC<SyncStatusBadgeProps> = ({
  status,
  size = 'sm',
  showLabel = false,
  className = '',
}) => {
  const configs = {
    pending: {
      color: 'bg-amber-100 text-amber-800',
      icon: <div className="w-2 h-2 rounded-full bg-amber-500" />,
      label: 'Pending',
    },
    syncing: {
      color: 'bg-blue-100 text-blue-800',
      icon: <RefreshCw className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} animate-spin`} />,
      label: 'Syncing',
    },
    synced: {
      color: 'bg-green-100 text-green-800',
      icon: <Cloud className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'}`} />,
      label: 'Synced',
    },
    conflict: {
      color: 'bg-red-100 text-red-800',
      icon: <AlertCircle className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'}`} />,
      label: 'Conflict',
    },
    error: {
      color: 'bg-red-100 text-red-800',
      icon: <AlertCircle className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'}`} />,
      label: 'Error',
    },
  };

  const config = configs[status];

  return (
    <span
      className={`
        inline-flex items-center rounded-full font-medium
        ${size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm'}
        ${config.color}
        ${className}
      `}
    >
      {config.icon}
      {showLabel && <span className="ml-1">{config.label}</span>}
    </span>
  );
};

// Pending Changes Panel
interface PendingChangesPanelProps {
  changes: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: Date;
  }>;
  onRetry?: (id: string) => void;
  onDiscard?: (id: string) => void;
  className?: string;
}

export const PendingChangesPanel: React.FC<PendingChangesPanelProps> = ({
  changes,
  onRetry,
  onDiscard,
  className = '',
}) => {
  if (changes.length === 0) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 text-center ${className}`}>
        <Cloud className="h-8 w-8 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">All changes synced</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">
          Pending Changes ({changes.length})
        </h3>
      </div>
      <ul className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
        {changes.map((change) => (
          <li key={change.id} className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {change.description}
                </p>
                <p className="text-xs text-gray-500">
                  {change.type} • {change.timestamp.toLocaleTimeString()}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                {onRetry && (
                  <button
                    onClick={() => onRetry(change.id)}
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                )}
                {onDiscard && (
                  <button
                    onClick={() => onDiscard(change.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
